import { useState, useCallback } from 'react'

export interface SearchResult {
  title: string
  url: string
  snippet: string
  source: string
  is_video: boolean
  is_tutorial: boolean
  relevance_score: number
}

export interface ItemAnalysis {
  item_id: number
  item_type: string
  title: string
  stuck_reason: string
  days_stuck: number
  micro_steps: string[]
  encouragement: string
  search_queries: string[]
}

export interface MotivationNudge {
  timestamp: string
  stuck_items_count: number
  items_analysis: ItemAnalysis[]
  overall_message: string
  horton_wisdom: string
  search_results: Record<string, SearchResult[]>
  nudge: {
    final_message: string
    highlighted_resources: Array<{ title: string; url: string; reason: string }>
    suggested_action: string
    follow_up_question: string | null
  }
}

export interface NudgeResponse {
  success: boolean
  has_nudge: boolean
  nudge?: MotivationNudge
  message?: string
  error?: string
}

export function useMotivatorNudge() {
  const [nudge, setNudge] = useState<MotivationNudge | null>(null)
  const [hasNudge, setHasNudge] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fetched, setFetched] = useState(false)

  const fetchNudge = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const res = await fetch('/api/orchestrator/nudge', {
        credentials: 'include',
      })
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`)
      }
      const data: NudgeResponse = await res.json()
      if (data.success) {
        setHasNudge(data.has_nudge)
        setNudge(data.nudge ?? null)
      } else {
        setError(data.error ?? 'Failed to fetch nudge')
      }
    } catch (err) {
      console.warn('Failed to fetch motivation nudge:', err)
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
      setFetched(true)
    }
  }, [])

  return {
    nudge,
    hasNudge,
    loading,
    error,
    fetched,
    fetchNudge,
  }
}
